'use client';

import { Tabs } from '@base-ui-components/react/tabs';
import type { ListDetail } from '@swishapp/api-client';
import { useQuery } from '@tanstack/react-query';
import { type ComponentType, type ReactNode, useState } from 'react';
import { useWishlist } from '.';
import {
  AddItemsToBoardDialog,
  BoardCard,
  CreateBoardDialog,
  DeleteBoardDialog,
  RenameBoardDialog,
  useWishlistItemsWithProducts,
} from '../../components/pages/wishlist/client';
import type { WishlistItemWithProduct } from '../../components/pages/wishlist/client/wishlist-types';
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from '../../components/responsive-dialog';
import { Button } from '../../components/ui/button';
import { DialogFooter } from '../../components/ui/dialog';
import type {
  SharedWishlistCardData,
  WishlistCardItem,
} from '../../components/wishlist-card';
import { cn } from '../../lib/utils';
import { useWishlistCacheInvalidation } from './hooks/use-wishlist-cache-invalidation';
import { useWishlistMutations } from './hooks/use-wishlist-mutations';
import { useWishlistSessionToken } from './hooks/use-wishlist-session-token';
import { WISHLIST_QUERY_KEYS } from './wishlist-constants';
import { WishlistProxyClient } from './wishlist-proxy-client';

function FallbackWishlistItem({ item }: { item: WishlistItemWithProduct }) {
  const { removeItemMutation } = useWishlistMutations();

  const handleRemove = () => {
    removeItemMutation.mutate(item.id);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-col space-y-2">
        <div>
          <div className="font-medium text-gray-600 text-sm">
            Product ID: {item.productId}
          </div>
          <div className="text-gray-500 text-xs">Product data unavailable</div>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={removeItemMutation.isPending}
          className={cn(
            'self-start text-red-600 text-xs hover:text-red-800',
            removeItemMutation.isPending && 'cursor-not-allowed opacity-50'
          )}
          aria-label={`Remove product ${item.productId} from wishlist`}
        >
          {removeItemMutation.isPending
            ? 'Removing...'
            : 'Remove from Wishlist'}
        </button>
      </div>
    </div>
  );
}

function BoardFallbackWishlistItem({
  item,
  boardId,
}: {
  item: WishlistItemWithProduct;
  boardId: string;
}) {
  const { removeItemFromBoardMutation } = useWishlistMutations();

  const handleRemove = () => {
    removeItemFromBoardMutation.mutate({ itemId: item.id, boardId });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-col space-y-2">
        <div>
          <div className="font-medium text-gray-600 text-sm">
            Product ID: {item.productId}
          </div>
          <div className="text-gray-500 text-xs">Product data unavailable</div>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={removeItemFromBoardMutation.isPending}
          className={cn(
            'self-start text-red-600 text-xs hover:text-red-800',
            removeItemFromBoardMutation.isPending &&
              'cursor-not-allowed opacity-50'
          )}
          aria-label={`Remove product ${item.productId} from board`}
        >
          {removeItemFromBoardMutation.isPending
            ? 'Removing...'
            : 'Remove from Board'}
        </button>
      </div>
    </div>
  );
}

function WishlistDialogRoot({ children }: { children: ReactNode }) {
  return <ResponsiveDialog>{children}</ResponsiveDialog>;
}

function WishlistTrigger({ children }: { children: ReactNode }) {
  return <ResponsiveDialogTrigger asChild>{children}</ResponsiveDialogTrigger>;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="text-center">
        <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-[hsl(var(--color))] border-b-2" />
        <p className="text-gray-500 text-sm">Loading your wishlist...</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-8 text-center">
      <svg
        className="mx-auto mb-4 h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <title>Empty Wishlist</title>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
      <p className="mb-1 font-medium text-gray-900 text-lg">
        Your wishlist is empty
      </p>
      <p className="text-gray-500 text-sm">
        Start adding products you love to see them here
      </p>
    </div>
  );
}

function ErrorState({ error }: { error: Error }) {
  return (
    <div className="py-8 text-center">
      <div className="mb-2 text-red-500">⚠️</div>
      <p className="text-gray-600 text-sm">
        Something went wrong loading your wishlist
      </p>
      <p className="mt-1 text-gray-400 text-xs">{error.message}</p>
    </div>
  );
}

function VariantIssueWishlistItem({ item }: { item: WishlistItemWithProduct }) {
  const { removeItemMutation } = useWishlistMutations();

  const handleRemove = () => {
    removeItemMutation.mutate(item.id);
  };

  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
      <div className="flex flex-col space-y-2">
        <div>
          <div className="font-medium text-orange-700 text-sm">
            Variant Issue
          </div>
          <div className="text-orange-600 text-xs">
            Product ID: {item.productId} • Variant ID: {item.variantId}
          </div>
          <div className="text-orange-500 text-xs">
            This variant may have been discontinued
          </div>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={removeItemMutation.isPending}
          className={cn(
            'self-start text-orange-600 text-xs hover:text-orange-800',
            removeItemMutation.isPending && 'cursor-not-allowed opacity-50'
          )}
          aria-label={`Remove discontinued variant ${item.variantId} of product ${item.productId} from wishlist`}
        >
          {removeItemMutation.isPending
            ? 'Removing...'
            : 'Remove from Wishlist'}
        </button>
      </div>
    </div>
  );
}

function BoardVariantIssueWishlistItem({
  item,
  boardId,
}: {
  item: WishlistItemWithProduct;
  boardId: string;
}) {
  const { removeItemFromBoardMutation } = useWishlistMutations();

  const handleRemove = () => {
    removeItemFromBoardMutation.mutate({ itemId: item.id, boardId });
  };

  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
      <div className="flex flex-col space-y-2">
        <div>
          <div className="font-medium text-orange-700 text-sm">
            Variant Issue
          </div>
          <div className="text-orange-600 text-xs">
            Product ID: {item.productId} • Variant ID: {item.variantId}
          </div>
          <div className="text-orange-500 text-xs">
            This variant may have been discontinued
          </div>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={removeItemFromBoardMutation.isPending}
          className={cn(
            'self-start text-orange-600 text-xs hover:text-orange-800',
            removeItemFromBoardMutation.isPending &&
              'cursor-not-allowed opacity-50'
          )}
          aria-label={`Remove discontinued variant ${item.variantId} of product ${item.productId} from board`}
        >
          {removeItemFromBoardMutation.isPending
            ? 'Removing...'
            : 'Remove from Board'}
        </button>
      </div>
    </div>
  );
}

function ItemsList({
  wishlistCard,
}: {
  wishlistCard: ComponentType<{
    product: SharedWishlistCardData;
    item: WishlistCardItem;
    boardId?: string;
    className?: string;
  }>;
}) {
  const {
    items,
    isLoading,
    error,
    itemsWithMissingProducts,
    itemsWithVariantIssues,
  } = useWishlistItemsWithProducts();
  const WishlistCard = wishlistCard;

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (
    items.length === 0 &&
    itemsWithMissingProducts.length === 0 &&
    itemsWithVariantIssues.length === 0
  )
    return <EmptyState />;

  return (
    <div className="max-h-96 overflow-y-auto">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {items.map(
          (item) =>
            item.product && (
              <WishlistCard key={item.id} product={item.product} item={item} />
            )
        )}

        {/* Show fallback for items with missing product data */}
        {itemsWithMissingProducts.map((item) => (
          <FallbackWishlistItem key={item.id} item={item} />
        ))}

        {/* Show variant issue items */}
        {itemsWithVariantIssues.map((item) => (
          <VariantIssueWishlistItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function BoardsList({
  onBoardClick,
  primaryButtonClassName,
  secondaryButtonClassName,
}: {
  onBoardClick: (board: ListDetail) => void;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}) {
  const { lists, loading } = useWishlist();

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {/* Create Board Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-[hsl(var(--color))] text-sm">
          Your Boards
        </h3>
        <CreateBoardDialog
          primaryButtonClassName={primaryButtonClassName}
          secondaryButtonClassName={secondaryButtonClassName}
        >
          <Button size="sm" className={primaryButtonClassName}>
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>New Board</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Board
          </Button>
        </CreateBoardDialog>
      </div>

      {/* Boards Grid */}
      {lists && lists.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {lists.map((board) => (
            <BoardCard key={board.id} board={board} onClick={onBoardClick} />
          ))}
        </div>
      ) : (
        <div className="py-12 text-center">
          <div className="mb-4 text-gray-400">
            <svg
              className="mx-auto h-16 w-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>No Boards</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <p className="mb-2 text-[hsl(var(--color))] text-lg">No boards yet</p>
          <p className="mb-6 text-gray-500 text-sm">
            Create boards to organize your wishlist items
          </p>
          <CreateBoardDialog
            primaryButtonClassName={primaryButtonClassName}
            secondaryButtonClassName={secondaryButtonClassName}
          >
            <Button className={primaryButtonClassName}>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Create Your First Board</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Your First Board
            </Button>
          </CreateBoardDialog>
        </div>
      )}
    </div>
  );
}

function BoardItemsList({
  boardItems,
  boardId,
  wishlistCard,
}: {
  boardItems: {
    id: string;
    productId: number;
    variantId: number;
    quantity: number;
  }[];
  boardId: string;
  wishlistCard: ComponentType<{
    product: SharedWishlistCardData;
    item: WishlistCardItem;
    boardId?: string;
    className?: string;
  }>;
}) {
  const {
    items,
    isLoading,
    error,
    itemsWithMissingProducts,
    itemsWithVariantIssues,
  } = useWishlistItemsWithProducts();
  const WishlistCard = wishlistCard;

  // Only show loading if we have no board items and still loading
  // This allows optimistic updates to show immediately
  if (isLoading && boardItems.length === 0) return <LoadingState />;
  if (error && boardItems.length === 0) return <ErrorState error={error} />;

  // Match board items with full product data
  const boardItemsWithProducts = boardItems
    .map((boardItem) => {
      // Find matching item with product data
      const matchingItem = items.find((item) => item.id === boardItem.id);
      if (matchingItem?.product) {
        return { ...matchingItem, product: matchingItem.product };
      }

      // Check if it's a missing product item
      const missingItem = itemsWithMissingProducts.find(
        (item) => item.id === boardItem.id
      );
      if (missingItem) {
        return missingItem;
      }

      // Check if it's a variant issue item
      const variantIssueItem = itemsWithVariantIssues.find(
        (item) => item.id === boardItem.id
      );
      if (variantIssueItem) {
        return variantIssueItem;
      }

      // For optimistic items, they should now have full product data
      // but if not, return the basic structure for fallback
      if (
        boardItem.id.startsWith('optimistic-') ||
        boardItem.id.startsWith('instant-')
      ) {
        return boardItem; // Should have full product data now
      }

      return null;
    })
    .filter(Boolean);

  return (
    <div className="max-h-96 overflow-y-auto">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {boardItemsWithProducts.map((item) => {
          if (!item) return null;

          // Type guard: check if item has product property (it's a WishlistItemWithProduct)
          if (
            'product' in item &&
            item.product &&
            typeof item.product === 'object' &&
            '_id' in item.product
          ) {
            // Convert to WishlistCardItem format
            const cardItem = {
              id: item.id,
              productId: item.productId,
              variantId: item.variantId,
              quantity: item.quantity,
            };
            return (
              <WishlistCard
                key={item.id}
                product={item.product as SharedWishlistCardData}
                item={cardItem}
                boardId={boardId}
              />
            );
          }

          // Check if it's a variant issue item
          const isVariantIssue = itemsWithVariantIssues.some(
            (variantItem) => variantItem.id === item.id
          );
          if (isVariantIssue) {
            // Find the matching variant issue item with full type
            const variantIssueItem = itemsWithVariantIssues.find(
              (variantItem) => variantItem.id === item.id
            );
            if (variantIssueItem) {
              return (
                <BoardVariantIssueWishlistItem
                  key={item.id}
                  item={variantIssueItem}
                  boardId={boardId}
                />
              );
            }
          }

          // Check if it's a missing product item
          const missingProductItem = itemsWithMissingProducts.find(
            (missingItem) => missingItem.id === item.id
          );
          if (missingProductItem) {
            return (
              <BoardFallbackWishlistItem
                key={item.id}
                item={missingProductItem}
                boardId={boardId}
              />
            );
          }

          // Fallback: shouldn't reach here, but handle gracefully
          return null;
        })}
      </div>
    </div>
  );
}

function BoardDetailView({
  board,
  onBack,
  onBoardEdit,
  onBoardDelete,
  primaryButtonClassName,
  secondaryButtonClassName,
  wishlistCard,
}: {
  board: ListDetail;
  onBack: () => void;
  onBoardEdit?: (board: ListDetail) => void;
  onBoardDelete?: (boardId: string) => void;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
  wishlistCard: ComponentType<{
    product: SharedWishlistCardData;
    item: WishlistCardItem;
    boardId?: string;
    className?: string;
  }>;
}) {
  // Fetch current board data to ensure it's up-to-date with cache changes
  const { data: authToken } = useWishlistSessionToken();
  const { data: currentBoard } = useQuery({
    queryKey: [WISHLIST_QUERY_KEYS.LIST, board.id, authToken],
    queryFn: async () => {
      if (!authToken) throw new Error('No auth token available');
      const client = new WishlistProxyClient(authToken);
      const result = await client.getList(board.id);
      if (result.error) throw new Error(String(result.error));
      return result.data;
    },
    enabled: !!authToken,
    initialData: board, // Use the prop as initial data
    staleTime: 2 * 60 * 1000, // 2 minutes - allow optimistic updates to show
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use current board data or fallback to prop
  const activeBoard = currentBoard || board;
  const boardItems = (activeBoard.items || []).map((item) => ({
    id: item.id,
    productId: item.productId,
    variantId: typeof item.variantId === 'number' ? item.variantId : 0,
    quantity: item.quantity,
  }));
  const { deleteListMutation, updateListMutation } = useWishlistMutations();
  const {
    invalidateAfterListChange,
    invalidateAfterItemChange,
    removeListFromCache,
  } = useWishlistCacheInvalidation();
  const [activeDialog, setActiveDialog] = useState<
    'rename' | 'delete' | 'addItems' | null
  >(null);

  const handleRenameBoard = async (newName: string) => {
    try {
      await updateListMutation.mutateAsync({
        listId: activeBoard.id,
        input: { name: newName.trim() },
      });
      // Update local state immediately
      onBoardEdit?.({ ...activeBoard, name: newName.trim() });
      // Invalidate cache to ensure UI consistency
      invalidateAfterListChange(activeBoard.id);
    } catch {
      // Silently handle error - mutation error state will be handled by UI
    }
  };

  const handleDeleteBoard = async () => {
    try {
      await deleteListMutation.mutateAsync(activeBoard.id);
      // Remove from cache immediately to prevent stale data
      removeListFromCache(activeBoard.id);
      // Invalidate lists query to update the board list
      invalidateAfterListChange();
      onBoardDelete?.(activeBoard.id);
      onBack(); // Go back to boards list after deletion
    } catch {
      // Silently handle error - mutation error state will be handled by UI
    }
  };

  return (
    <div>
      {/* Header with back button and actions */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Back</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h3 className="font-semibold text-[hsl(var(--color))] text-lg">
              {activeBoard.name}
            </h3>
            <p className="text-gray-500 text-sm">
              {boardItems.length} {boardItems.length === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>

        {/* Board Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveDialog('addItems')}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
            title="Add items to board"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Add Items</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setActiveDialog('rename')}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
            title="Rename board"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Rename</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setActiveDialog('delete')}
            className="flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-gray-100 hover:text-red-700"
            title="Delete board"
            disabled={deleteListMutation.isPending}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Delete</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Board items */}
      {boardItems.length > 0 ? (
        <BoardItemsList
          boardItems={boardItems}
          boardId={activeBoard.id}
          wishlistCard={wishlistCard}
        />
      ) : (
        <div className="py-12 text-center">
          <div className="mb-4 text-gray-400">
            <svg
              className="mx-auto h-16 w-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Empty Board</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </div>
          <p className="mb-2 text-[hsl(var(--color))] text-lg">
            This board is empty
          </p>
          <p className="mb-4 text-gray-500 text-sm">
            Add products to this board from your wishlist
          </p>
          <Button
            className={cn('mt-4', primaryButtonClassName)}
            onClick={() => setActiveDialog('addItems')}
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <title>Add Items</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Items to Board
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <RenameBoardDialog
        board={activeBoard}
        open={activeDialog === 'rename'}
        onOpenChange={(open) => setActiveDialog(open ? 'rename' : null)}
        onRename={handleRenameBoard}
        isRenaming={updateListMutation.isPending}
        primaryButtonClassName={primaryButtonClassName}
        secondaryButtonClassName={secondaryButtonClassName}
      />

      <DeleteBoardDialog
        board={activeBoard}
        open={activeDialog === 'delete'}
        onOpenChange={(open) => setActiveDialog(open ? 'delete' : null)}
        onDelete={handleDeleteBoard}
        isDeleting={deleteListMutation.isPending}
        primaryButtonClassName={primaryButtonClassName}
        secondaryButtonClassName={secondaryButtonClassName}
      />

      <AddItemsToBoardDialog
        board={activeBoard}
        isOpen={activeDialog === 'addItems'}
        onOpenChange={(open) => setActiveDialog(open ? 'addItems' : null)}
        onItemsAdded={() => {
          // Trigger cache invalidation to refresh board items and all items
          invalidateAfterItemChange(activeBoard.id);
        }}
        primaryButtonClassName={primaryButtonClassName}
        secondaryButtonClassName={secondaryButtonClassName}
        wishlistCard={wishlistCard}
      />
    </div>
  );
}

function WishlistContent({
  wishlistCard,
  className,
  primaryButtonClassName,
  secondaryButtonClassName,
}: {
  wishlistCard: ComponentType<{
    product: SharedWishlistCardData;
    item: WishlistCardItem;
    boardId?: string;
    className?: string;
  }>;
  className?: string;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}) {
  const [selectedBoard, setSelectedBoard] = useState<ListDetail | null>(null);

  return (
    <ResponsiveDialogContent
      title="Your Wishlist"
      description="Products you've saved for later"
      className={cn('max-h-[80vh] w-full max-w-4xl', className)}
    >
      <Tabs.Root defaultValue="items">
        <Tabs.List className="mb-4 flex items-center gap-4 border-b px-4">
          <Tabs.Tab
            value="items"
            className={cn(
              'px-1 py-2 font-medium text-sm transition-colors data-[selected]:border-[hsl(var(--color))] data-[selected]:border-b-2 data-[selected]:text-[hsl(var(--color))]',
              'text-muted-foreground hover:text-[hsl(var(--color))] data-[focus-visible]:outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-[hsl(var(--color))] data-[focus-visible]:ring-offset-2'
            )}
          >
            All Items
          </Tabs.Tab>
          <Tabs.Tab
            value="boards"
            className={cn(
              'px-1 py-2 font-medium text-sm transition-colors data-[selected]:border-[hsl(var(--color))] data-[selected]:border-b-2 data-[selected]:text-[hsl(var(--color))]',
              'text-muted-foreground hover:text-[hsl(var(--color))] data-[focus-visible]:outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-[hsl(var(--color))] data-[focus-visible]:ring-offset-2'
            )}
          >
            Boards
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="items">
          <div className="px-4">
            <ItemsList wishlistCard={wishlistCard} />
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="boards">
          <div className="px-4">
            {selectedBoard ? (
              <BoardDetailView
                board={selectedBoard}
                onBack={() => setSelectedBoard(null)}
                onBoardEdit={(editedBoard) => {
                  setSelectedBoard(editedBoard);
                }}
                onBoardDelete={() => {
                  setSelectedBoard(null);
                }}
                primaryButtonClassName={primaryButtonClassName}
                secondaryButtonClassName={secondaryButtonClassName}
                wishlistCard={wishlistCard}
              />
            ) : (
              <BoardsList
                onBoardClick={(board) => setSelectedBoard(board)}
                primaryButtonClassName={primaryButtonClassName}
                secondaryButtonClassName={secondaryButtonClassName}
              />
            )}
          </div>
        </Tabs.Panel>
      </Tabs.Root>

      <DialogFooter className="mt-6">
        <ResponsiveDialogClose asChild>
          <Button className={secondaryButtonClassName}>Close</Button>
        </ResponsiveDialogClose>
      </DialogFooter>
    </ResponsiveDialogContent>
  );
}

WishlistDialogRoot.Trigger = WishlistTrigger;
WishlistDialogRoot.Content = WishlistContent;

export const WishlistDialog = WishlistDialogRoot;
